import Fade from '@components/Animation/Fade';
import ArrowIcon from '@components/ArrowIcon';
import {getRefElement} from '@utils/refUtils';
import throttle from 'lodash.throttle';
import type {MouseEvent, ReactNode, RefObject, SyntheticEvent} from 'react';
import {Component, createRef} from 'react';
import {TransitionGroup} from 'react-transition-group';

const DEFAULT_IMAGE_HEIGHT = 500;
const THROTTLE_INTERVAL = 300;
const IMAGE_CLICK_WIDTH_THRESHOLD = 2;
const VERTICAL_MARGIN_BASE = 16;

interface Props {
    children: HTMLImageElement[];
}

interface State {
    active: number;
    imageHeight: number;
}

export default class PhotoSlider extends Component<Props, State> {
    public readonly state: State = {
        active: 0,
        imageHeight: DEFAULT_IMAGE_HEIGHT,
    };

    private readonly imageRef: RefObject<HTMLImageElement> = createRef();

    private readonly handleResize = throttle(() => {
        try {
            const image = getRefElement(this.imageRef);
            this.setState({
                imageHeight: image.offsetHeight,
            });
        } catch (refError) {
            console.error(refError);
        }
    }, THROTTLE_INTERVAL);

    private static getAttribute(item: HTMLImageElement, name: string) {
        return item.getAttribute(name);
    }

    public componentDidMount() {
        window.addEventListener('resize', this.handleResize);
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    private readonly handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
        this.setState({imageHeight: event.currentTarget.offsetHeight});
    };

    private readonly handleImageClick = (event: MouseEvent<HTMLImageElement>) => {
        if (
            event.nativeEvent.offsetX <
            event.currentTarget.offsetWidth / IMAGE_CLICK_WIDTH_THRESHOLD
        ) {
            this.switchBack();
        } else {
            this.switchForward();
        }
    };

    private readonly handlePreviousClick = () => {
        this.switchBack();
    };

    private readonly handleNextClick = () => {
        this.switchForward();
    };

    private switchBack() {
        this.setState(prevState => ({
            active: prevState.active <= 0 ? this.props.children.length - 1 : prevState.active - 1,
        }));
    }

    private switchForward() {
        this.setState(prevState => ({
            active: prevState.active + 1 >= this.props.children.length ? 0 : prevState.active + 1,
        }));
    }

    public render(): ReactNode {
        return (
            <div className="slider">
                <div className="slider__navigation">
                    <button
                        type="button"
                        className="slider__arrow"
                        onClick={this.handlePreviousClick}
                    >
                        <ArrowIcon left />
                    </button>
                    <span className="slider__slide-number">
                        {this.state.active + 1}/{this.props.children.length}
                    </span>
                    <button type="button" className="slider__arrow" onClick={this.handleNextClick}>
                        <ArrowIcon right />
                    </button>
                </div>
                <TransitionGroup
                    className="slider__photo-container"
                    style={{height: this.state.imageHeight}}
                >
                    <Fade key={this.state.active}>
                        <img
                            className="slider__photo"
                            src={
                                PhotoSlider.getAttribute(
                                    this.props.children[this.state.active],
                                    'src',
                                )!
                            }
                            srcSet={
                                PhotoSlider.getAttribute(
                                    this.props.children[this.state.active],
                                    'srcset',
                                ) ?? ''
                            }
                            sizes={
                                PhotoSlider.getAttribute(
                                    this.props.children[this.state.active],
                                    'sizes',
                                ) ?? ''
                            }
                            alt={
                                PhotoSlider.getAttribute(
                                    this.props.children[this.state.active],
                                    'alt',
                                ) ?? ''
                            }
                            onClick={this.handleImageClick}
                            onLoad={this.handleImageLoad}
                            ref={this.imageRef}
                            loading="lazy"
                        />
                    </Fade>
                </TransitionGroup>
                <TransitionGroup style={{marginTop: VERTICAL_MARGIN_BASE}}>
                    <Fade key={`${this.state.active}_alt`}>
                        <div className="slider__description">
                            {this.props.children[this.state.active].alt}
                        </div>
                    </Fade>
                </TransitionGroup>
            </div>
        );
    }
}
